import React, { useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import { setOpenPopup } from '#action/project/devices-popup';
import DevicePopup from '#component/device/devices-popup/device-popup-project';
import {
  DeviceFieldContainer,
  Title,
  TitleDisabled,
  DevicesChipsContainer,
  ButtonsContainer,
  AddButtonEnabled,
  AddButtonDisabled,
  ModifyButton,
  ErrorText
} from './styles';
import { DeviceChip } from './device-chip';
import DeviceSearch from './search/device-search';

const DeviceField = ({
  disabled,
  formData,
  allFormDevices,
  handleDeviceSetData,
  devicePopupData = {},
  meta: fieldMeta = {},
  forceError,
  isApplianceDeviceAlign,
  isApplianceStop,
  DevicePopupComponent = DevicePopup,
  alertId
}) => {
  const dispatch = useDispatch();
  const meta = useSelector(s => s.meta, shallowEqual);
  const isDevicePopupOpen = useSelector(s =>
    s.projectDevicesPopup?.[devicePopupData?.member], shallowEqual);
  const showModifyButton = formData?.length > 10;
  const title = formData?.length ? `Devices (${formData.length})` : 'Devices';

  const rawError = fieldMeta.error;
  // eslint-disable-next-line no-underscore-dangle
  const errorMessage = typeof rawError === 'object' ? (rawError?.message || rawError?._error) : rawError;
  const isTouchedOrSubmitted = Boolean(
    fieldMeta.touched || fieldMeta.isTouched ||
    fieldMeta.isSubmitted || fieldMeta.submitFailed || fieldMeta.isDirty
  );
  const showValidationError = Boolean(errorMessage && (isTouchedOrSubmitted || forceError));

  const handleOpenDevicePopup = (e, addMode) => {
    dispatch(setOpenPopup(true, devicePopupData?.member, addMode));
  };

  const handleRemoveDevice = device => {
    const newDevices = formData.filter(d => d.id !== device.id);
    handleDeviceSetData(newDevices);
  };

  const onAlertChange = useCallback((event, newValue) => {
    const newData = formData.map(d => {
      if (d.id === newValue.id) {
        return {
          ...d,
          alertOn: !d.alertOn
        };
      }

      return d;
    });

    handleDeviceSetData(newData);
  }, [formData, handleDeviceSetData]);

  const TitleComponent = disabled ? TitleDisabled : Title;
  const AddBtn = (disabled || isApplianceStop) ? AddButtonDisabled : AddButtonEnabled;

  return (
    <>
      <DeviceFieldContainer hasError={showValidationError} isAligned={isApplianceDeviceAlign}>
        <div>
          <TitleComponent>{title}</TitleComponent>
          <DevicesChipsContainer>
            {formData?.length <= 10 && formData.map(device => (
              <DeviceChip
                device={device}
                meta={meta}
                handleRemoveDevice={handleRemoveDevice}
                handleDeviceSetData={handleDeviceSetData}
                onAlertChange={onAlertChange}
                key={device.id}
                disabled={disabled || isApplianceStop}
              />
            ))}
          </DevicesChipsContainer>
        </div>
        <ButtonsContainer>
          <AddBtn
            size="small"
            color="secondary"
            aria-label="Add devices"
            disabled={disabled || isApplianceStop}
            onClick={e => handleOpenDevicePopup(e, true)}
          >
            <AddIcon />
            <span>Add devices</span>
          </AddBtn>
          {showModifyButton ?
            <ModifyButton
              size="small"
              color="secondary"
              aria-label="Modify devices"
              onClick={handleOpenDevicePopup}
            >
              <span>{`Modify devices (${formData.length})`}</span>
            </ModifyButton> :
            <DeviceSearch
              handleDeviceSetData={handleDeviceSetData}
              formData={formData}
              allFormDevices={allFormDevices}
              disabled={disabled || isApplianceStop}
              alertId={alertId}
            />}
        </ButtonsContainer>
        {showValidationError && <ErrorText>{errorMessage}</ErrorText>}
      </DeviceFieldContainer>
      {(isDevicePopupOpen) &&
        <DevicePopupComponent
          {...devicePopupData}
          open={isDevicePopupOpen}
          emptyForm={!formData?.length}
        />}
    </>
  );
};

export default DeviceField;
